// Services
import { apiInstance, gql } from "@/services/httpAdapter";

// Helpers
import { errorLogger } from "@Common/helpers/logger";

// Fragments
import { RequestStatusFragment, VehicleFragment, VehicleInventoryFragment } from "./fragments/dealerInventory";

// Types
import { Nullable } from "@Common/interfaces/types";
import { GraphqlQueryResponse } from "@Common/api/types/GraphqlQueryResponse";
import { IBasicPaginatedQueryResponse, IPageParam, IPaginatedResponse } from "@/Common/api/types/pagination";
import { IAdministrationTools } from "@Settings/types/administrationTools/model";
import { Inventory } from "@DataPages/types/inventory/models/inventory";
import { ISummaryDetail } from "@DataPages/types/inventory/models/reconciliation";
import { KMS } from "@/DataPages/types/inventory/models/kms";
import { TCapitalizedVehicleType } from "@DataPages/types/inventory/commons";
import {
	IInventoryFilterOptions,
	IInventoryTradeVehicles,
	IPendingRequest,
	TFilterOptionsArgs,
	TSearchTransferRequest,
	ICustomerRegistrationStatusGroup,
} from "@/DataPages/components/Inventory/types/dealerTrades";

export async function getVehicles({
	locationId,
	commissionCycleId,
	payPeriodId,
	startDate,
	endDate,
	vehicleCategory,
	first,
	after,
}: {
	locationId: string;
	commissionCycleId: string;
	payPeriodId: string;
	startDate: Date;
	endDate: Date;
	vehicleCategory: string;
	first: number;
	after: string;
}) {
	const query = gql`
		query getVehicles(
			$locationId: String
			$commissionCycleId: String
			$payPeriodId: String
			$startDate: AWSDateTime
			$endDate: AWSDateTime
			$vehicleCategory: String
			$pagination: PaginationInput
		) {
			getVehicles(
				locationId: $locationId
				commissionCycleId: $commissionCycleId
				payPeriodId: $payPeriodId
				startDate: $startDate
				endDate: $endDate
				vehicleCategory: $vehicleCategory
				pagination: $pagination
			) {
				items {
					...Vehicle
				}
				pageInfo {
					endCursor
					hasNextPage
				}
			}
		}
		${VehicleInventoryFragment}
	`;

	const variables = {
		locationId,
		commissionCycleId,
		payPeriodId,
		startDate,
		endDate,
		vehicleCategory,
		pagination: {
			first,
			after: after ?? "",
		},
	};

	try {
		const response: GraphqlQueryResponse<"getVehicles", IBasicPaginatedQueryResponse<Inventory>> = await apiInstance.post("/graphql", {
			query,
			variables,
		});
		return response.data.data?.getVehicles;
	} catch (err) {
		errorLogger(err as Error, { function: "getVehicles", message: "Failed to get getVehicles", variables });
		return null;
	}
}

export async function getKeyManagement(locationId: string, payPeriodId: string, pagination: IPageParam<string>) {
	const query = gql`
		query getKeyEvents($payPeriodId: String!, $locationId: String!, $pagination: PaginationInput) {
			getKeyEvents(payPeriodId: $payPeriodId, locationId: $locationId, pagination: $pagination) {
				edges {
					cursor
					node {
						_id
						kmsId
						vinNumber
						assetId
						stockId
						transactionId
						eventType
						createdBy
						completionDate
						creationDate
						kioskId
						reason
						images
						timeOut
						pastDue
					}
				}
				pageInfo {
					endCursor
					hasNextPage
				}
			}
		}
	`;

	const variables = {
		locationId,
		payPeriodId,
		pagination,
	};

	try {
		const response: GraphqlQueryResponse<"getKeyEvents", IPaginatedResponse<KMS>> = await apiInstance.post("/graphql", {
			query,
			variables,
		});
		return response.data.data?.getKeyEvents;
	} catch (err) {
		errorLogger(err as Error, { function: "getKeyEvents", message: "Failed to get getKeyEvents", variables });
		return null;
	}
}
export async function getKeyEventImagesByKiosk(locationId: string, kioskId: string, eventDate: string): Promise<string[] | null> {
	const query = gql`
		query getKeyEventImagesByKiosk($kioskId: String!, $locationId: String!, $eventDate: String!) {
			getKeyEventImagesByKiosk(kioskId: $kioskId, locationId: $locationId, eventDate: $eventDate)
		}
	`;

	const variables = {
		kioskId,
		locationId,
		eventDate,
	};

	try {
		const response: GraphqlQueryResponse<"getKeyEventImagesByKiosk", string[]> = await apiInstance.post("/graphql", {
			query,
			variables,
		});
		return response.data.data?.getKeyEventImagesByKiosk ?? [];
	} catch (err) {
		errorLogger(err as Error, { function: "getKeyEventImagesByKiosk", message: "Failed to get getKeyEventImagesByKiosk", variables });
		return null;
	}
}

export async function getVehicleDetails(locationId: string, inventoryId: string): Promise<Inventory | null> {
	if (!inventoryId || !locationId) {
		return null;
	}
	const query = gql`
		query getVehicleDetails($inventoryId: String!, $locationId: String!) {
			getVehicleDetails(inventoryId: $inventoryId, locationId: $locationId) {
				_id
				properties {
					dataType
					displayName
					precision
					icon
					value
				}
			}
		}
	`;

	const variables = {
		locationId,
		inventoryId,
	};

	try {
		const response: GraphqlQueryResponse<"getVehicleDetails", Inventory> = await apiInstance.post("/graphql", {
			query,
			variables,
		});
		return response.data.data?.getVehicleDetails ?? null;
	} catch (err) {
		errorLogger(err as Error, { function: "getVehicleDetails", message: "Failed to get getVehicleDetails", variables });
		return null;
	}
}
export async function getVehicleGPSDetails(locationId: string, inventoryId: string, accountId: string): Promise<Inventory | null> {
	if (!inventoryId || !locationId) {
		return null;
	}
	const query = gql`
		query getVehicleGPSDetails($inventoryId: String!, $locationId: String!, $accountId: String!) {
			getVehicleGPSDetails(inventoryId: $inventoryId, locationId: $locationId, accountId: $accountId) {
				_id
				properties {
					dataType
					displayName
					precision
					icon
					value
				}
				sourceUrl
			}
		}
	`;

	const variables = {
		locationId,
		inventoryId,
		accountId,
	};

	try {
		const response: GraphqlQueryResponse<"getVehicleGPSDetails", Inventory> = await apiInstance.post("/graphql", {
			query,
			variables,
		});
		return response.data.data?.getVehicleGPSDetails ?? null;
	} catch (err) {
		errorLogger(err as Error, { function: "getVehicleGPSDetails", message: "Failed to get getVehicleGPSDetails", variables });
		return null;
	}
}

export async function getGPSEmbeddedURL(locationId: string, accountId: string, vins: string[] = []) {
	const query = gql`
		query getGPSEmbeddedURL($locationId: String!, $accountId: String!, $vins: [String]) {
			getGPSEmbeddedURL(locationId: $locationId, accountId: $accountId, vins: $vins)
		}
	`;

	const variables = {
		locationId,
		accountId,
		vins,
	};

	try {
		const response: GraphqlQueryResponse<"getGPSEmbeddedURL", string> = await apiInstance.post("/graphql", { query, variables });
		return response.data.data?.getGPSEmbeddedURL;
	} catch (error) {
		errorLogger(error as Error);
		return null;
	}
}

export async function getInventoryFilterOptions(locationId: string, filterOptionsInput: TFilterOptionsArgs) {
	const query = gql`
		query getInventoryFilterOptions($locationId: String!, $filterOptionsInput: InventoryFilterOptionsInput!) {
			getInventoryFilterOptions(locationId: $locationId, filterOptionsInput: $filterOptionsInput) {
				Make
				Model
				TRIM
				Type
				Year
				locationIds
			}
		}
	`;

	const variables = {
		locationId,
		filterOptionsInput,
	};

	try {
		const response: GraphqlQueryResponse<"getInventoryFilterOptions", IInventoryFilterOptions[]> = await apiInstance.post("/graphql", {
			query,
			variables,
		});

		return response.data.data?.getInventoryFilterOptions;
	} catch (error) {
		errorLogger(error as Error);
		return null;
	}
}

export async function getInventoryTradeVehicles(
	locationId: string,
	filters: Partial<TSearchTransferRequest>,
	pagination: { first: number; after: string }
) {
	const query = gql`
		query getInventoryTradeVehicles($locationId: String!, $filters: SearchTransferRequest, $pagination: PaginationInput) {
			getInventoryTradeVehicles(locationId: $locationId, filters: $filters, pagination: $pagination) {
				edges {
					node {
						_id
						locationId
						make
						model
						stock
						trim
						colorExterior
						type
						vin
						year
						transferAmount
						status
						invoiceCost
						tradingWith {
							_id
							isTradeActive
							isBlocked
						}
					}
					cursor
				}
				pageInfo {
					endCursor
					hasNextPage
				}
			}
		}
	`;

	const variables = {
		locationId,
		filters,
		pagination,
	};

	try {
		const response: GraphqlQueryResponse<
			"getInventoryTradeVehicles",
			IPaginatedResponse<IInventoryTradeVehicles>
		> = await apiInstance.post("/graphql", {
			query,
			variables,
		});
		return response.data.data?.getInventoryTradeVehicles;
	} catch (error) {
		errorLogger(error as Error);
		return null;
	}
}

export const getDealerInventoryPendingRequest = async (locationId: string, commissionCycleId = "", payPeriodId = "") => {
	const query = gql`
		query getDealerInventoryPendingRequest($locationId: String!, $commissionCycleId: String!, $payPeriodId: String!) {
			getDealerInventoryPendingRequest(locationId: $locationId, commissionCycleId: $commissionCycleId, payPeriodId: $payPeriodId) {
				transferRequests {
					_id
					commissionCycleId
					destinationLocationId
					originLocationId
					payPeriodId
					requestId
					swapId
					requestedByLocationId
					transferType
					isBlocked
					requestStatus {
						...RequestStatus
					}
					vehicle {
						...Vehicle
					}
				}
			}
		}
		${RequestStatusFragment}
		${VehicleFragment}
	`;
	const variables = {
		locationId,
		commissionCycleId,
		payPeriodId,
	};

	try {
		const response: GraphqlQueryResponse<"getDealerInventoryPendingRequest", IPendingRequest> = await apiInstance.post("/graphql", {
			query,
			variables,
		});
		return response.data.data?.getDealerInventoryPendingRequest;
	} catch (error) {
		errorLogger(error as Error);
		return null;
	}
};

export const getDealerInventoryTransferHistory = async (locationId: string, commissionCycleId = "", payPeriodId = "") => {
	const query = gql`
		query getDealerInventoryTransferHistory($locationId: String!, $commissionCycleId: String!, $payPeriodId: String!) {
			getDealerInventoryTransferHistory(locationId: $locationId, commissionCycleId: $commissionCycleId, payPeriodId: $payPeriodId) {
				transferRequests {
					_id
					commissionCycleId
					destinationLocationId
					originLocationId
					payPeriodId
					requestId
					swapId
					requestStatus {
						...RequestStatus
					}
					requestedByLocationId
					transferType
					vehicle {
						...Vehicle
					}
				}
			}
		}
		${RequestStatusFragment}
		${VehicleFragment}
	`;
	const variables = {
		locationId,
		commissionCycleId,
		payPeriodId,
	};

	try {
		const response: GraphqlQueryResponse<"getDealerInventoryTransferHistory", IPendingRequest> = await apiInstance.post("/graphql", {
			query,
			variables,
		});
		return response.data.data?.getDealerInventoryTransferHistory;
	} catch (error) {
		errorLogger(error as Error);
		return null;
	}
};

type TGetVehicleDetailsArgs = {
	locationId: string;
	commissionCycleId: string;
	payPeriodId: string;
	vehicleType: TCapitalizedVehicleType;
};
export async function getReconciliationSummary({ locationId, commissionCycleId, payPeriodId, vehicleType }: TGetVehicleDetailsArgs) {
	const query = gql`
		query getReconciliationSummary($locationId: String!, $commissionCycleId: String!, $payPeriodId: String!, $vehicleType: String!) {
			getReconciliationSummary(
				locationId: $locationId
				commissionCycleId: $commissionCycleId
				payPeriodId: $payPeriodId
				vehicleType: $vehicleType
			) {
				storeNumber
				inventoryDate
				reconciliationCompletionDate
				dealership
				# Below properties are common for all types of vehicles (New and Used)
				physicalInventoryCountOnLot
				physicalInventoryCountOffLot
				unitsOnScheduleNotOnLot
				unitsOnLotNotOnSchedule
				unitsOffLotNotOnSchedule
				perGeneralLedgerSchedule
				totalSchedule
				differenceSchedule
				# Below properties are only for new vehicles
				physicalInventoryOnLot
				physicalInventoryOffLot
				unitsInPhysicalInventoryNotOnMfg
				unitsOffPhysicalInventoryNotOnMfg
				perManufacturerReport
				totalManufacture
				differenceManufacture

				locationId
				commissionCycleId
				payPeriodId
				vehicleType
			}
		}
	`;

	const variables = {
		locationId,
		commissionCycleId,
		payPeriodId,
		vehicleType,
	};

	try {
		const response: GraphqlQueryResponse<"getReconciliationSummary", ISummaryDetail> = await apiInstance.post("/graphql", {
			query,
			variables,
		});
		return response.data.data?.getReconciliationSummary;
	} catch (error) {
		errorLogger(error as Error);
		return null;
	}
}

export async function getGlobalPositioningServiceProvider(locationId: string): Promise<Nullable<IAdministrationTools>> {
	const query = gql`
		query getAdministrationTools($locationId: String!) {
			getAdministrationTools(locationId: $locationId) {
				integrations {
					globalPositioningService {
						lojack {
							providerName
							gpsStoreId
							gpsStoreName
						}
					}
				}
			}
		}
	`;

	const variables = {
		locationId,
	};

	try {
		const response: GraphqlQueryResponse<"getAdministrationTools", IAdministrationTools> = await apiInstance.post("/graphql", {
			query,
			variables,
		});
		return response.data.data?.getAdministrationTools;
	} catch (error) {
		errorLogger(error as Error);
		return null;
	}
}

export async function getConsumerRegistrationStatus(
	locationId: string,
	commissionCycleId: string,
	payPeriodId: string,
	pagination: IPageParam<string>
) {
	const query = gql`
		query getConsumerRegistrationStatus(
			$locationId: String!
			$commissionCycleId: String!
			$payPeriodId: String!
			$pagination: PaginationInput
		) {
			getConsumerRegistrationStatus(
				locationId: $locationId
				commissionCycleId: $commissionCycleId
				payPeriodId: $payPeriodId
				pagination: $pagination
			) {
				edges {
					node {
						parent {
							_id
							firstName
							lastName
							email
							vehicleVin
							vehicleYear
							vehicleMake
							vehicleModel
							invitationDateCreated
							invitationExpirationDate
							invitationStatus
							dateImported
							stockNumber
							dealNumber
							businessPart
							units
							salesEmployeeId
							salesEmployeeName
							financeEmployeeId
							financeManagerName
							salesManagerId
							dealManagerName
							dealStatus
							saleDate
							saleType
							dealType
							updatedAt
						}
						children {
							_id
							firstName
							lastName
							email
							vehicleVin
							vehicleYear
							vehicleMake
							vehicleModel
							invitationDateCreated
							invitationExpirationDate
							invitationStatus
							dateImported
							stockNumber
							dealNumber
							businessPart
							units
							salesEmployeeId
							salesEmployeeName
							financeEmployeeId
							financeManagerName
							salesManagerId
							dealManagerName
							dealStatus
							saleDate
							saleType
							dealType
							updatedAt
						}
					}
					cursor
				}
				pageInfo {
					endCursor
					hasNextPage
				}
			}
		}
	`;

	const variables = {
		locationId,
		commissionCycleId,
		payPeriodId,
		pagination,
	};

	try {
		const response: GraphqlQueryResponse<
			"getConsumerRegistrationStatus",
			IPaginatedResponse<ICustomerRegistrationStatusGroup>
		> = await apiInstance.post("/graphql", {
			query,
			variables,
		});
		return response.data.data?.getConsumerRegistrationStatus;
	} catch (error) {
		errorLogger(error as Error);
		return null;
	}
}
